import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "./useDebounce";
import { useSaveEmailDraftMutation } from "../api/queries";
import { draftFeedbackQueryKey } from "../api/queries/draftFeedbackQueries";

const normalizeEmails = (values: string[]) =>
  values.map((value) => value.trim()).filter(Boolean);

const normalizeAndDedupeEmails = (values: string[]) =>
  Array.from(new Set(normalizeEmails(values)));

interface UseAutoSaveDraftParams {
  invoiceId: string | undefined;
  draftUid?: string | null;
  emailContent: string;
  subject: string;
  to: string[];
  cc: string[];
  bcc: string[];
  threadId?: string | null;
  replyMessageId?: string | null;
}

export const useAutoSaveDraft = ({
  invoiceId,
  draftUid,
  emailContent,
  subject,
  to,
  cc,
  bcc,
  threadId,
  replyMessageId,
}: UseAutoSaveDraftParams) => {
  const [saveStatus, setSaveStatus] = useState<"saving" | "saved" | null>(null);
  const queryClient = useQueryClient();
  const saveEmailDraftMutation = useSaveEmailDraftMutation();
  const lastSavedStateRef = useRef<string | null>(null);
  const isFirstRender = useRef(true);

  // Normalize and create a state object for debouncing
  // This will change whenever any of the inputs change, resetting the debounce timer
  const normalizedTo = normalizeAndDedupeEmails(to);
  const normalizedCc = normalizeAndDedupeEmails(cc);
  const normalizedBcc = normalizeAndDedupeEmails(bcc);

  // Create a combined draft state object for debouncing
  // Use sorted arrays joined as strings for consistent comparison
  const draftState = {
    emailContent: emailContent.trim(),
    subject: subject.trim(),
    to: normalizedTo.sort().join(","),
    cc: normalizedCc.sort().join(","),
    bcc: normalizedBcc.sort().join(","),
  };

  // Serialize the current state for initial comparison
  const currentStateString = JSON.stringify(draftState);

  // Debounce the draft state with 3 second delay
  // This will reset the timer every time the state changes
  // Only after 3 seconds of no changes will debouncedDraftState update
  const debouncedDraftState = useDebounce(draftState, 3000);

  useEffect(() => {
    // Skip on first render to avoid saving immediately on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // Store the initial state as the last saved state
      lastSavedStateRef.current = currentStateString;
      return;
    }

    // Skip if no invoiceId
    if (!invoiceId) {
      return;
    }

    // Skip if email is empty or no recipients
    if (normalizedTo.length === 0 || !emailContent.trim()) {
      return;
    }

    // Serialize the debounced state for comparison
    const debouncedStateString = JSON.stringify(debouncedDraftState);

    // Only save if the debounced state is different from the last saved state
    // This ensures we don't save if nothing actually changed
    if (debouncedStateString === lastSavedStateRef.current) {
      return;
    }

    const saveDraft = async () => {
      setSaveStatus("saving");
      try {
        // Parse back the normalized arrays from the debounced state
        const debouncedTo = debouncedDraftState.to
          ? debouncedDraftState.to.split(",").filter(Boolean)
          : [];
        const debouncedCc = debouncedDraftState.cc
          ? debouncedDraftState.cc.split(",").filter(Boolean)
          : [];
        const debouncedBcc = debouncedDraftState.bcc
          ? debouncedDraftState.bcc.split(",").filter(Boolean)
          : [];

        await saveEmailDraftMutation.mutateAsync({
          paymentExternalId: invoiceId,
          draftData: {
            text: debouncedDraftState.emailContent,
            to: debouncedTo,
            subject: debouncedDraftState.subject,
            cc: debouncedCc.length > 0 ? debouncedCc : null,
            bcc: debouncedBcc.length > 0 ? debouncedBcc : null,
            thread_id: threadId ?? null,
            reply_message_id: replyMessageId ?? null,
          },
        });
        if (draftUid) {
          void queryClient.invalidateQueries({
            queryKey: draftFeedbackQueryKey(draftUid),
          });
        }
        // Update the last saved state after successful save
        lastSavedStateRef.current = debouncedStateString;
        setSaveStatus("saved");
        // Clear the "saved" status after 2 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 2000);
      } catch (error) {
        // Silently handle errors - just log, don't show toast
        console.error("Auto-save draft failed:", error);
        setSaveStatus(null);
      }
    };

    saveDraft();
  }, [debouncedDraftState, invoiceId, threadId, replyMessageId]);

  return saveStatus;
};
